import { LogRecord } from "@fairfleet/geotab";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import L from "leaflet";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CircleMarker, MapContainer, Polyline, TileLayer, Tooltip, useMap } from "react-leaflet";
import { MotorValues, motorValuesAt } from "../models/routeMotorData";
import {
  RoutePoint,
  SPEED_COLORS,
  TripRange,
  deviceOrder,
  speedColor,
  speedRange,
  toRoutePoints,
  toRouteSegments,
  toTripMarkers,
  vehicleColor,
} from "../models/routeTrack";
import { QueryDiagnosticDeviceData } from "@/models/QueryDiagnosticDeviceData";
import "leaflet/dist/leaflet.css";

/** Enough of a gap that the outermost points are not under a legend. */
const FIT_PADDING: [number, number] = [40, 40];
/** Above Leaflet's own panes, which sit in the four hundreds. */
const OVERLAY_Z_INDEX = 500;
const START_MARKER_COLOR = "#2e7d32";
const STOP_MARKER_COLOR = "#c62828";

/**
 * Puts the map back around the route.
 *
 * Only when `fitKey` changes — that is, when a different set of journeys was picked. Fitting on
 * every render would undo the reader's own panning and zooming a moment after they did it.
 */
function FitToRoute({ points, fitKey }: { points: RoutePoint[]; fitKey: string }) {
  const map = useMap();

  useEffect(() => {
    if (points.length === 0) {
      return;
    }

    const bounds = L.latLngBounds(points.map(point => [point.latitude, point.longitude]));
    map.invalidateSize();
    map.fitBounds(bounds, { padding: FIT_PADDING });
    // Only a different set of journeys should move the map, not every position that arrives.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitKey, map]);

  return null;
}

/**
 * Tells Leaflet its container changed size.
 *
 * Leaflet works out its tile grid once and does not watch its own box, so anything that resizes
 * the map — dragging the divider between the list and the map, or the window itself — leaves a
 * blank strip along the edge until it is told. Answered on the next frame, because a drag fires
 * this continuously.
 */
function InvalidateOnResize() {
  const map = useMap();

  useEffect(() => {
    const container = map.getContainer();
    const observed = container.parentElement ?? container;
    let frame = 0;

    const refresh = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => map.invalidateSize());
    };

    refresh();
    const observer = new ResizeObserver(refresh);
    observer.observe(observed);
    window.addEventListener("resize", refresh);

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener("resize", refresh);
    };
  }, [map]);

  return null;
}

/** A box over the map, for the legends and the point details. */
function MapOverlay({ corner, children }: { corner: "topRight" | "bottomLeft"; children: ReactNode }) {
  return (
    <Paper
      elevation={3}
      sx={{
        position: "absolute",
        zIndex: OVERLAY_Z_INDEX,
        ...(corner === "topRight" ? { top: 12, right: 12 } : { bottom: 12, left: 12 }),
        p: 1.25,
        minWidth: 200,
        maxWidth: 260,
        pointerEvents: "none",
      }}
    >
      {children}
    </Paper>
  );
}

/** One line of the point details, left out entirely when the vehicle reported nothing. */
function DetailLine({ label, value }: { label: string; value: string | null }) {
  if (value === null) {
    return null;
  }

  return (
    <Typography variant="caption" display="block">
      {`${label}: ${value}`}
    </Typography>
  );
}

interface Props {
  logRecords: LogRecord[];
  /** The journeys that were picked, which is what separates them from the rest of the day. */
  tripRanges: TripRange[];
  /** What to call each vehicle in the legend. */
  deviceLabels: Record<string, string>;
  /** One colour per vehicle instead of a colour per speed. */
  monochrome: boolean;
  /** The engine readings, shown for whichever point the reader clicks. */
  statusData?: QueryDiagnosticDeviceData;
}

/**
 * The driven route on a map.
 *
 * Leaflet rather than the deck.gl stack the live map uses. This map draws a line over tiles and
 * is done; it has no thousands of moving markers to keep on the GPU, and Leaflet's own polyline
 * is a line rather than a layer to configure.
 */
export function TripRouteMap({
  logRecords,
  tripRanges,
  deviceLabels,
  monochrome,
  statusData,
}: Props) {
  const { t, i18n } = useTranslation();
  const [selected, setSelected] = useState<RoutePoint | null>(null);

  const points = useMemo(() => toRoutePoints(logRecords, tripRanges), [logRecords, tripRanges]);
  const segments = useMemo(() => toRouteSegments(points), [points]);
  const speeds = useMemo(() => speedRange(points), [points]);
  const order = useMemo(() => deviceOrder(points), [points]);
  const markers = useMemo(() => toTripMarkers(tripRanges), [tripRanges]);

  /** Changes only when a different set of journeys is picked. */
  const fitKey = useMemo(
    () =>
      [...tripRanges]
        .map(range => range.tripId)
        .sort()
        .join("|"),
    [tripRanges],
  );

  // Only ever for the one point that was clicked: a day of positions is tens of thousands of
  // records, and the readings are never shown for more than one of them.
  const motor: MotorValues | null = useMemo(
    () => (selected ? motorValuesAt(statusData, selected.deviceId, selected.time) : null),
    [selected, statusData],
  );

  const dateFormat = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "medium" }),
    [i18n.language],
  );

  // A point picked from a journey that has since been unticked no longer exists on this map.
  useEffect(() => {
    setSelected(current =>
      current && points.some(point => point.time === current.time && point.deviceId === current.deviceId)
        ? current
        : null,
    );
  }, [points]);

  if (points.length < 2) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ height: "100%", p: 2 }}>
        <Typography variant="body2" color="text.secondary" align="center">
          {t("route.noRouteData")}
        </Typography>
      </Stack>
    );
  }

  const showVehicleLegend = monochrome && order.length > 1;

  return (
    <Box sx={{ position: "relative", height: "100%", width: "100%" }}>
      <MapContainer
        center={[points[0].latitude, points[0].longitude]}
        zoom={13}
        style={{ height: "100%", width: "100%" }}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        />
        <InvalidateOnResize />
        <FitToRoute points={points} fitKey={fitKey} />

        {segments.map(segment => (
          <Polyline
            key={`${segment.deviceId}-${segment.from.time}`}
            positions={[
              [segment.from.latitude, segment.from.longitude],
              [segment.to.latitude, segment.to.longitude],
            ]}
            pathOptions={{
              color: monochrome
                ? vehicleColor(segment.deviceId, order)
                : speedColor(segment.speed, speeds.min, speeds.max),
              weight: 5,
              opacity: 0.9,
            }}
            eventHandlers={{ click: () => setSelected(segment.to) }}
          />
        ))}

        {markers.flatMap(marker => {
          const inRange = points.filter(
            point =>
              point.deviceId === marker.deviceId &&
              point.time >= marker.start.getTime() &&
              point.time <= marker.stop.getTime(),
          );
          const first = inRange[0];
          const last = inRange[inRange.length - 1];

          if (!first || !last) {
            return [];
          }

          return [
            <CircleMarker
              key={`${marker.tripId}-start`}
              center={[first.latitude, first.longitude]}
              radius={9}
              pathOptions={{ color: START_MARKER_COLOR, fillColor: START_MARKER_COLOR, fillOpacity: 1 }}
            >
              <Tooltip permanent direction="top" offset={[0, -6]} opacity={1}>
                {marker.number}
              </Tooltip>
            </CircleMarker>,
            <CircleMarker
              key={`${marker.tripId}-stop`}
              center={[last.latitude, last.longitude]}
              radius={6}
              pathOptions={{ color: STOP_MARKER_COLOR, fillColor: STOP_MARKER_COLOR, fillOpacity: 1 }}
            />,
          ];
        })}
      </MapContainer>

      {showVehicleLegend ? (
        <MapOverlay corner="topRight">
          <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>
            {t("route.vehicleLegend")}
          </Typography>
          {order.map(deviceId => (
            <Stack key={deviceId} direction="row" spacing={0.75} alignItems="center">
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  bgcolor: vehicleColor(deviceId, order),
                  flexShrink: 0,
                }}
              />
              <Typography variant="caption" noWrap>
                {deviceLabels[deviceId] ?? deviceId}
              </Typography>
            </Stack>
          ))}
        </MapOverlay>
      ) : null}

      {monochrome ? null : (
        <MapOverlay corner="topRight">
          <Typography variant="caption" sx={{ fontWeight: 600, display: "block" }}>
            {t("route.speedLegend")}
          </Typography>
          <Box
            sx={{
              height: 8,
              borderRadius: 1,
              my: 0.5,
              background: `linear-gradient(to right, ${SPEED_COLORS.join(", ")})`,
            }}
          />
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="caption">
              {t("route.speedValue", { value: Math.round(speeds.min) })}
            </Typography>
            <Typography variant="caption">
              {t("route.speedValue", { value: Math.round(speeds.max) })}
            </Typography>
          </Stack>
        </MapOverlay>
      )}

      <MapOverlay corner="bottomLeft">
        <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>
          {t("route.pointDetails")}
        </Typography>

        {selected && motor ? (
          <>
            <DetailLine label={t("route.time")} value={dateFormat.format(new Date(selected.time))} />
            <DetailLine
              label={t("route.location")}
              value={t("route.coordinates", {
                lat: selected.latitude.toFixed(5),
                lng: selected.longitude.toFixed(5),
              })}
            />
            <DetailLine
              label={t("route.speed")}
              value={t("route.speedValue", { value: Math.round(selected.speed) })}
            />
            <DetailLine
              label={t("route.engineSpeed")}
              value={
                motor.engineSpeed === null
                  ? null
                  : t("route.engineSpeedValue", { value: Math.round(motor.engineSpeed) })
              }
            />
            <DetailLine
              label={t("route.ignition")}
              value={motor.ignition === null ? null : motor.ignition ? t("yes") : t("no")}
            />
            <DetailLine
              label={t("route.engineRoadSpeed")}
              value={
                motor.engineRoadSpeed === null
                  ? null
                  : t("route.speedValue", { value: Math.round(motor.engineRoadSpeed) })
              }
            />
            <DetailLine
              label={t("route.fuelLevel")}
              value={
                motor.fuelLevel === null
                  ? null
                  : t("route.percentValue", { value: Math.round(motor.fuelLevel) })
              }
            />
            <DetailLine
              label={t("route.adBlueLevel")}
              value={
                motor.adBlueLevel === null
                  ? null
                  : t("route.percentValue", { value: Math.round(motor.adBlueLevel) })
              }
            />
            <DetailLine
              label={t("route.soc")}
              value={motor.soc === null ? null : t("route.percentValue", { value: Math.round(motor.soc) })}
            />
          </>
        ) : (
          <Typography variant="caption" color="text.secondary">
            {t("route.selectMapPoint")}
          </Typography>
        )}
      </MapOverlay>
    </Box>
  );
}
